/**
 * Per-device UI preferences (theme, density, layout…). These are deliberately
 * local: they describe *this* screen, not the account. Account-level settings
 * (display name, signature, remote content) live server-side instead.
 *
 * Every storage/DOM access is guarded so this module is safe to import from
 * tests running without a browser.
 */
import { effect, signal } from "@preact/signals";

export type ThemePref = "system" | "light" | "dark";
export type Density = "comfortable" | "compact";
export type ReadingPane = "right" | "off";
export type AfterMove = "next" | "list";
export type Locale = "es" | "en";

export interface UiPrefs {
  theme: ThemePref;
  density: Density;
  readingPane: ReadingPane;
  /** Seconds to hold a message before it is really sent (0 = send immediately). */
  undoSendSeconds: number;
  /** What to show after archiving/deleting the open message. */
  afterMove: AfterMove;
  /** Show body previews under the subject in the list. */
  showPreviews: boolean;
  /** Desktop notifications for new mail while the tab is in the background. */
  notifications: boolean;
  locale: Locale;
  sidebarCollapsed: boolean;
  /** Width of the message list column in pixels (desktop, reading pane on the right). */
  listWidth: number;
}

const STORAGE_KEY = "twm.prefs.v1";

function detectLocale(): Locale {
  try {
    return (navigator.language || "en").toLowerCase().startsWith("es") ? "es" : "en";
  } catch {
    return "en";
  }
}

export const DEFAULT_PREFS: UiPrefs = {
  theme: "system",
  density: "comfortable",
  readingPane: "right",
  undoSendSeconds: 5,
  afterMove: "next",
  showPreviews: true,
  notifications: false,
  locale: "en",
  sidebarCollapsed: false,
  listWidth: 400,
};

function load(): UiPrefs {
  const base = { ...DEFAULT_PREFS, locale: detectLocale() };
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<UiPrefs>;
    return { ...base, ...parsed };
  } catch {
    return base;
  }
}

export const prefs = signal<UiPrefs>(load());

export function setPref<K extends keyof UiPrefs>(key: K, value: UiPrefs[K]): void {
  prefs.value = { ...prefs.value, [key]: value };
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(prefs.value));
  } catch {
    /* private mode / quota: the preference just won't survive a reload */
  }
}

/** Resolve "system" against the OS setting. */
export const systemDark = signal(false);

export function resolvedTheme(): "light" | "dark" {
  const t = prefs.value.theme;
  if (t === "system") return systemDark.value ? "dark" : "light";
  return t;
}

/** Wire the preferences to <html> attributes. Call once from main. */
export function applyPrefsToDocument(): void {
  if (typeof document === "undefined") return;
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    systemDark.value = mq.matches;
    mq.addEventListener("change", (e) => (systemDark.value = e.matches));
  } catch {
    /* very old browsers: stay light */
  }
  effect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme();
    root.dataset.density = prefs.value.density;
    root.lang = prefs.value.locale;
  });
}
